<template>
  <v-container
    v-if="authenticationMixin.checkedLogin"
    class="content pa-0"
    fluid
  >
    <!-- Hero section -->
    <page-hero title="Administratie">
      <p class="page-hero-intro text-center">
        Alle belangrijke administratieve documenten op één plaats.
      </p>
      <p class="page-hero-hint text-center">
        Hiervoor heeft u Acrobat Reader nodig.
        U kan dit gratis <a class="secondaryLink" href="https://get.adobe.com/nl/reader/" target="_blank" rel="noopener noreferrer">downloaden</a>.
      </p>
    </page-hero>

    <!-- Content columns -->
    <v-row
      justify="center"
      class="mb-8 pb-8 admin-columns"
    >
      <!-- Informatiebrochure -->
      <v-col
        class="center px-4"
        cols="12"
        md="4"
      >
        <div class="admin-section">
          <div class="admin-section-header">
            <v-icon
              class="admin-section-icon"
              color="#907322"
              size="22"
            >
              mdi-book-open-variant
            </v-icon>
            <h2 class="admin-section-title">
              Informatiebrochure
            </h2>
          </div>
          <div
            class="admin-brochure clickAction"
            @click="OpenInformatiebrochure"
          >
            <v-img
              :src="require(`@/assets/images/Administratie informatiebrochure thumbnail.jpg`)"
              max-height="200px"
              contain
            />
          </div>
          <v-btn
            class="mt-4"
            color="primary"
            rounded
            block
            @click="OpenInformatiebrochure"
          >
            <v-icon
              class="mr-1"
              size="18"
            >
              mdi-book-open-page-variant
            </v-icon>
            Lezen
          </v-btn>
        </div>
      </v-col>

      <!-- Documenten & Facturen -->
      <v-col
        class="center px-4"
        cols="12"
        md="4"
      >
        <!-- Documenten / attesten -->
        <div class="admin-section">
          <div class="admin-section-header">
            <v-icon
              class="admin-section-icon"
              color="#907322"
              size="22"
            >
              mdi-file-document-outline
            </v-icon>
            <h2 class="admin-section-title">
              Documenten / attesten
            </h2>
          </div>
          <div class="admin-list">
            <a
              v-for="(item, index) in documentenAttesten"
              class="admin-list-item secondaryLink"
              :key="'doc-' + index"
              @click="DownloadFile(item)"
            >
              <v-icon
                color="#907322"
                size="18"
              >
                mdi-download
              </v-icon>
              <span>{{ item.naam }}</span>
            </a>
          </div>
        </div>

        <!-- Kostenraming / Facturen -->
        <div class="admin-section mt-6">
          <div class="admin-section-header">
            <v-icon
              class="admin-section-icon"
              color="#907322"
              size="22"
            >
              mdi-receipt-text-outline
            </v-icon>
            <h2 class="admin-section-title">
              Kostenraming / Facturen
            </h2>
          </div>
          <div class="admin-list">
            <template
              v-for="(item, index) in kostenramingFacturen"
              :key="'kf-' + index"
            >
              <a
                v-if="item.url"
                class="admin-list-item secondaryLink"
                @click="$router.push(item.url)"
              >
                <v-icon
                  color="#907322"
                  size="18"
                >
                  mdi-chevron-right
                </v-icon>
                <span>{{ item.naam }}</span>
              </a>
              <div
                v-else
                class="admin-list-item admin-list-item--collapsable"
                @click="Collapse(item)"
              >
                <v-icon
                  color="#907322"
                  size="18"
                >
                  mdi-chevron-{{ item.collapsed ? 'right' : 'down' }}
                </v-icon>
                <span>{{ item.naam }}</span>
              </div>
              <template v-if="item.collapsable && !item.collapsed">
                <a
                  v-for="(child, cIndex) in item.children"
                  class="admin-list-item admin-list-item--child secondaryLink"
                  :key="'kf-child-' + cIndex"
                  @click="DownloadFactuur(child)"
                >
                  <v-icon
                    color="#999"
                    size="16"
                  >
                    mdi-download
                  </v-icon>
                  <span>{{ child.bestandsnaam }}</span>
                </a>
              </template>
            </template>
          </div>
        </div>
      </v-col>

      <!-- Niet te vergeten -->
      <v-col
        class="center px-4"
        cols="12"
        md="4"
      >
        <div class="admin-section">
          <div class="admin-section-header">
            <v-icon
              class="admin-section-icon"
              color="#907322"
              size="22"
            >
              mdi-clipboard-check-outline
            </v-icon>
            <h2 class="admin-section-title">
              Niet te vergeten
            </h2>
          </div>
          <div class="admin-list">
            <template
              v-for="(item, index) in nietTeVergeten"
              :key="'ntv-' + index"
            >
              <div
                class="admin-list-item"
                :class="{ 'admin-list-item--collapsable': item.collapsable }"
                @click="Collapse(item)"
              >
                <v-icon
                  :color="item.collapsable ? '#907322' : '#999'"
                  size="18"
                >
                  {{ item.collapsable ? (item.collapsed ? 'mdi-chevron-right' : 'mdi-chevron-down') : 'mdi-circle-small' }}
                </v-icon>
                <span>{{ item.naam }}</span>
              </div>
              <template v-if="item.collapsable && !item.collapsed">
                <div
                  v-for="(child, cIndex) in item.children"
                  class="admin-list-item admin-list-item--child"
                  :key="'ntv-child-' + cIndex"
                >
                  <v-icon
                    color="#999"
                    size="16"
                  >
                    mdi-circle-small
                  </v-icon>
                  <span>{{ child.naam }}</span>
                </div>
              </template>
            </template>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PageHero from '@/components/PageHero.vue'
import authenticationMixin from '@/mixins/authenticationMixin'
import blobMixin from '@/mixins/blobMixin'
import commonMixin from '@/mixins/commonMixin'
import constantMixin from '@/mixins/constantMixin'
import errorMixin from '@/mixins/errorMixin'
import blobService from '@/services/blobService'
import klantService from '@/services/klantService'

export default {
  name: 'AdministratieComponent',
  components: {
    PageHero
  },
  mixins: [
    authenticationMixin,
    blobMixin,
    commonMixin,
    constantMixin,
    errorMixin
  ],
  data () {
    return {
      documentenAttesten: [
        {
          naam: 'Bewijs van kort verlet',
          subtype: 'bewijsVanKortVerlet'
        },
        {
          naam: 'Uittreksel overlijden',
          subtype: 'uittrekselVanOverlijden'
        }
      ],
      kostenramingFacturen: [
        {
          naam: 'Kostenraming',
          url: 'Facturatie'
        },
        {
          naam: 'Facturen',
          children: [],
          collapsable: true,
          collapsed: true
        }
      ],
      nietTeVergeten: [
        {
          naam: 'Nutsvoorzieningen',
          children: [
            { naam: 'Gas' },
            { naam: 'Water' },
            { naam: 'Elektriciteit' },
            { naam: 'Telefoon / gsm' },
            { naam: 'Internet' }
          ],
          collapsable: true,
          collapsed: true
        },
        {
          naam: 'Huur / verhuur'
        },
        {
          naam: 'Nummerplaat'
        },
        {
          naam: 'Automatisch (via rijksregister)',
          children: [
            { naam: 'Pensioen' },
            { naam: 'Ziekenfonds' }
          ],
          collapsable: true,
          collapsed: true
        }
      ]
    }
  },
  mounted () {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    this.AuthenticationMixin_CheckIfLoggedIn()
    blobService.Init(this)
    klantService.Init(this)
    this.GetFacturen()
  },
  methods: {
    Collapse (item) {
      if (item.collapsable) {
        item.collapsed = !item.collapsed
      }
    },
    async DownloadFactuur (factuur) {
      this.CommonMixin_Load(this.$options.name, true)
      const resp = await blobService.Download(factuur.idBlob)

      if (resp && (resp.data || resp.data !== '')) {
        this.BlobMixin_DownloadAction(factuur.bestandsnaam, null, resp.data)
      }

      this.CommonMixin_Load(this.$options.name, false)
    },
    async DownloadFile (item) {
      this.CommonMixin_Load(this.$options.name, true)
      const resp = await blobService.DownloadByBlobSearchCriteria({
        subtype: this.constantMixin.codes.facturatieEnDocBestandstype.values[item.subtype],
        type: this.constantMixin.codes.blobType.values.facturatie
      })

      if (resp && (resp.data || resp.data !== '')) {
        this.BlobMixin_DownloadAction(item.naam, null, resp.data)
      }

      this.CommonMixin_Load(this.$options.name, false)
    },
    async GetFacturen () {
      this.CommonMixin_Load(this.$options.name, true)
      const resp = await klantService.GetAdministratieFacturen()

      if (resp && (resp.data || resp.data !== '')) {
        const facturenObject = this.kostenramingFacturen.find(obj => obj.naam === 'Facturen')

        if (facturenObject) {
          facturenObject.children = resp.data
        }
      }

      this.CommonMixin_Load(this.$options.name, false)
    },
    async OpenInformatiebrochure () {
      const documentPath = (await import('@/assets/documents/Administratie informatiebrochure.pdf')).default
      window.open(`${documentPath}?t=${Date.now()}#page=1`, '_blank')
    }
  }
}
</script>

<style scoped>
.admin-columns {
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.admin-section {
  margin-bottom: 8px;
}

.admin-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 2px solid #DEC067;
}

.admin-section-title {
  font-size: 20px;
  font-weight: 500;
  color: #424E58;
  padding: 0;
  margin: 0;
}

.admin-brochure {
  border: 1px solid #e8e4dc;
  border-radius: 10px;
  overflow: hidden;
  padding: 12px;
  background-color: #fdfcfa;
  transition: background-color 0.2s ease;
}

.admin-brochure:hover {
  background-color: #FBF7EE;
}

.admin-list {
  display: flex;
  flex-direction: column;
}

.admin-list-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 8px;
  border-bottom: 1px solid #e8e4dc;
  font-size: 15px;
  color: #424E58;
  text-decoration: none;
}

.admin-list-item--collapsable {
  cursor: pointer;
}

.admin-list-item--collapsable:hover {
  background-color: #FBF7EE;
}

.admin-list-item--child {
  padding-left: 28px;
  font-size: 14px;
  color: #6b6b6b;
}
</style>
